import type { Dinero, DineroCurrency } from "dinero.js";
import {
  add,
  dinero,
  isNegative,
  isZero,
  multiply,
  subtract,
  toSnapshot,
} from "dinero.js";
import * as currencies from "dinero.js/currencies";

const DEFAULT_LOCALE = "en-US";

export class Money {
  private readonly dinero: Dinero<number>;
  private readonly locale: string;

  constructor(amount: number, currencyCode: string, locale?: string) {
    if (!Number.isInteger(amount)) {
      throw new Error(`amount is not an integer: ${amount}`);
    }

    const normalizedCurrencyCode = currencyCode.toUpperCase();
    try {
      const currencyInfo = Money.getCurrencyInfo(normalizedCurrencyCode);
      this.dinero = dinero({
        amount: amount,
        currency: currencyInfo,
      });
    } catch (err) {
      throw Error("couldn't create money:" + err);
    }

    this.locale = locale || navigator?.language || "en-US";
  }

  static getCurrencyInfo(currencyCode: string) {
    const currencyKey = currencyCode.toUpperCase();
    const currency = currencies[currencyKey as keyof typeof currencies];

    if (!currency) {
      throw new Error(`unsupported currency: ${currencyCode}`);
    }

    const result: DineroCurrency<number> & {
      isDecimal: boolean;
      symbol?: string;
    } = {
      ...currency,
      isDecimal: currency.exponent > 0,
    };

    const locale = navigator?.language || DEFAULT_LOCALE;
    try {
      const formatter = new Intl.NumberFormat(locale, {
        style: "currency",
        currency: currencyKey,
        currencyDisplay: "symbol",
      });
      const parts = formatter.formatToParts(1);
      const currencyPart = parts.find((p) => p.type === "currency");
      result.symbol = currencyPart?.value;
    } catch (err) {
      console.error("Couldn't get currency's symbol:", err);
    }

    return result;
  }

  static zero(currencyCode: string): Money {
    return new Money(0, currencyCode);
  }

  add(other: Money): Money {
    const result = add(this.dinero, other.dinero);
    return new Money(toSnapshot(result).amount, this.getCurrency());
  }

  subtract(other: Money): Money {
    const result = subtract(this.dinero, other.dinero);
    return new Money(toSnapshot(result).amount, this.getCurrency());
  }

  getAmount(): number {
    return toSnapshot(this.dinero).amount;
  }

  getCurrency(): string {
    return toSnapshot(this.dinero).currency.code;
  }

  format(): string {
    const snapshot = toSnapshot(this.dinero);
    const currency = snapshot.currency;
    const decimalValue = this.getAmount() / Math.pow(10, currency.exponent);
    return new Intl.NumberFormat(this.locale, {
      style: "currency",
      currency: this.getCurrency(),
    }).format(decimalValue);
  }

  isZero(): boolean {
    return isZero(this.dinero);
  }

  isNegative(): boolean {
    return isNegative(this.dinero);
  }

  negate(): Money {
    return new Money(
      toSnapshot(multiply(this.dinero, -1)).amount,
      this.getCurrency(),
    );
  }
}
